import { connect } from 'react-redux';
import { useMemo } from 'react';

import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmErknmPositionsSelectOptions } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getDecisionPositionsSelectOptions } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IDictionaryParticipantPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { IKnmFormAttachment } from 'KnmPackage/MainModule/interfaces/IKnmFormAttachment';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { IDecisionResultDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionResultDecisionForm';
import { IKnmPmFormResultDecision } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormResultDecision';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { SelectInput } from 'UIPackage/MainModule/components/SelectInput';

import styles from './ResultDecisionsItemContentCancelInfo.module.scss';

interface IResultDecisionsItemContentCancelInfoProps {
  fioSignerFieldName: string;
  titleSignerFieldName: string;
  cancelCommentFieldName: string;
  value: IKnmErknmFormResultDecision | IKnmPmFormResultDecision | IDecisionResultDecisionForm | undefined;
  cancelDocument?: IKnmFormAttachment;
  isDecision?: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isCancelSigned?: boolean;
}

interface IResultDecisionsItemContentCancelInfoStoreProps {
  positionsSelectOptions: IDictionaryPositionSelectOption[] | IDictionaryParticipantPositionSelectOption[] | undefined;
  decisionPositionsSelectOptions:
    | IDictionaryPositionSelectOption[]
    | IDictionaryParticipantPositionSelectOption[]
    | undefined;
}

const ResultDecisionsItemContentCancelInfoWithoutStore = ({
  positionsSelectOptions,
  fioSignerFieldName,
  titleSignerFieldName,
  cancelCommentFieldName,
  value,
  decisionPositionsSelectOptions,
  cancelDocument,
  isDecision,
  isKnm,
  isPm,
  isCancelSigned,
}: IResultDecisionsItemContentCancelInfoProps & IResultDecisionsItemContentCancelInfoStoreProps) => {
  const { downloadKnmDocumentAttachment } = useKnmDocumentAttachment();
  const document = useMemo(() => {
    const attach: IKnmFormAttachment | undefined = cancelDocument;

    return attach || null;
  }, [cancelDocument]);

  const downloadAttachment = () => {
    if (!document) return null;

    const { fileName, fileSize } = document;
    const filePath = document.filePath || null;
    const mimeType = document.mimeType || null;

    downloadKnmDocumentAttachment({ fileName, filePath, fileSize, mimeType });
  };

  const fioAndPositionSignerFieldName = `${fioSignerFieldName}AndPosition`;

  const mergedFioAndPosition = useMemo(() => {
    const fio = value?.cancelFioSigner ?? '';
    const title = value?.cancelTitleSigner?.label ?? '';
    const result = `${fio} - ${title}`;

    return result;
  }, [value?.cancelFioSigner, value?.cancelTitleSigner?.label]);

  const displayOption = useMemo(() => {
    return [
      {
        id: mergedFioAndPosition,
        value: mergedFioAndPosition,
        label: mergedFioAndPosition,
      },
    ];
  }, [mergedFioAndPosition]);

  const selectedOption = displayOption.length > 0 ? displayOption[0] : null;

  if (!isCancelSigned || !cancelDocument) return null;

  return (
    <>
      <div style={{ marginTop: '44px' }}></div>
      <FieldBlock label={`Сведения об отмене решения`}>
        <div className={styles.Wrapper}>
          <FieldBlock label={''}>
            <div className={styles.ActDocument}>
              <div className={styles.InfoBlock}>
                {document && (
                  <FieldBlock
                    label={'Файл'}
                    color="black"
                    placementLabel="left"
                    type="not-space"
                    className={styles.FieldBlock}
                  >
                    <UploadFile
                      fileName={document?.fileName || ''}
                      fileSize={document?.fileSize || 0}
                      onFileNameClick={downloadAttachment}
                    />
                  </FieldBlock>
                )}
              </div>
            </div>
          </FieldBlock>
        </div>
        <FieldBlock
          label={<>Комментарий</>}
          placementLabel="left"
          color="black"
          className={styles.FieldBlock}
          margin="bottom"
        >
          <TextareaField
            name={cancelCommentFieldName}
            size="medium"
            resize="vertical"
            inputClassName={styles.CopyReasonTextarea}
            autoHeight={true}
            disabled={true}
          />
        </FieldBlock>
        {false && (
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: 'ФИО',
              field: fioSignerFieldName,
              isDecision,
              isKnm,
              isPm,
            })}
            color="black"
            className={styles.FieldBlock}
            placementLabel="left"
            margin="bottom"
          >
            <TextInputField name={fioSignerFieldName} disabled={true} />
          </FieldBlock>
        )}
        {false && (
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: 'Должность',
              field: titleSignerFieldName,
              isDecision,
              isKnm,
              isPm,
            })}
            color="black"
            className={styles.FieldBlock}
            placementLabel="left"
            margin="bottom"
          >
            <SelectInputField
              name={titleSignerFieldName}
              placeholder="Выберите должность"
              options={isDecision ? decisionPositionsSelectOptions : positionsSelectOptions}
              isSearchable
              disabled={true}
              size="medium"
            />
          </FieldBlock>
        )}
        <FieldBlock
          label={getTitleFieldWithRedStar({
            label: 'ФИО подписавшего отмену решения',
            field: fioAndPositionSignerFieldName,
            isDecision,
            isKnm,
            isPm,
          })}
          color="black"
          className={styles.FieldBlock}
          placementLabel="left"
          margin="bottom"
        >
          <SelectInput
            value={selectedOption}
            options={displayOption}
            getOptionValue={opt => opt.value}
            isClearable={false}
            disabled={true}
            size="medium"
          />
        </FieldBlock>
      </FieldBlock>
    </>
  );
};

const mapStateToProps = (state: IRootState): IResultDecisionsItemContentCancelInfoStoreProps => ({
  positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
  decisionPositionsSelectOptions: getDecisionPositionsSelectOptions(state),
});

export const ResultDecisionsItemContentCancelInfo = connect(mapStateToProps)(
  ResultDecisionsItemContentCancelInfoWithoutStore,
);
